import type { AccessAndIDTokenResponse } from '@local/login/dist/apiClients/types';
import { setCombinedToken } from '@local/login/dist/store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import Grid from '@mui/material/GridLegacy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { useLegacyLoginMutation } from 'src/apiClients/imago-mp-api';
import { LEGACY_TOKEN_TYPE, ROUTES } from 'src/utils/strings';

import { legacyFormI18n as i18n } from '../Login.i18n';
import type { FieldType } from './credential-field/CredentialField';
import { CredentialField } from './credential-field/CredentialField';
import { ErrorMessage } from './error-message/ErrorMessage';
import { useStyles } from './LegacyLoginForm.styles';
import { SubmitButton } from './submit-button/SubmitButton';
import { SubscriptionWarning } from './subscription-warning/SubscriptionWarning';

type Props = {
    shouldDisplaySQIDError?: boolean;
    displayErrorEmail?: string;
};

interface Credentials {
    username: string;
    password: string;
}

const INITIAL_CREDENTIALS: Credentials = { username: '', password: '' };

const LegacyLoginForm = ({ shouldDisplaySQIDError = false, displayErrorEmail }: Props) => {
    const [credentials, setCredentials] = useState<Credentials>(INITIAL_CREDENTIALS);

    const { classes } = useStyles();
    const intl = useIntl();

    const [legacyLogin, { data, isLoading, isSuccess, isError }] = useLegacyLoginMutation();

    const apiToken = useMemo(() => data?.apiToken, [data]);

    useEffect(() => {
        if (isSuccess) {
            const imagoAccessToken = {
                access_token: apiToken,
                token_type: LEGACY_TOKEN_TYPE,
            } as AccessAndIDTokenResponse;

            setCombinedToken(imagoAccessToken);
        }
    }, [apiToken, isSuccess]);

    const handleFieldChange = useCallback((fieldType: FieldType, next: string) => {
        setCredentials((prev) => ({ ...prev, [fieldType]: next }));
    }, []);

    const handleLogin = useCallback(() => {
        legacyLogin(credentials);
    }, [legacyLogin, credentials]);

    if (isSuccess && apiToken && !isError) {
        return <Navigate to={ROUTES.ROOT_PATH} />;
    }

    return (
        <Grid
            container
            direction="column"
            className={classes.formContainer}
            automation-id="imago-id-form"
        >
            {shouldDisplaySQIDError && (
                <SubscriptionWarning
                    message={intl.formatMessage(i18n.subscriptionNotFound, {
                        email: displayErrorEmail,
                    })}
                    linkText={intl.formatMessage(i18n.sqidLink)}
                    className={classes.subscriptionMessage}
                />
            )}
            <CredentialField
                fieldType="username"
                value={credentials.username}
                label={intl.formatMessage(i18n.signInUsernamePlaceholder)}
                onChange={handleFieldChange}
                rowClassName={classes.formField}
                inputClassName={classes.textField}
            />
            <CredentialField
                fieldType="password"
                value={credentials.password}
                label={intl.formatMessage(i18n.signInPasswordPlaceholder)}
                onChange={handleFieldChange}
                rowClassName={classes.formField}
                inputClassName={classes.textField}
            />
            <SubmitButton
                label={intl.formatMessage(i18n.signInButtonText)}
                isLoading={isLoading}
                onClick={handleLogin}
                buttonClassName={classes.formButton}
                disabledClassName={classes.disabledButton}
                labelClassName={classes.buttonText}
                rowClassName={classes.formField}
            />
            {isError && (
                <ErrorMessage
                    message={intl.formatMessage(i18n.signInErrorText)}
                    className={classes.imagoIdErrorMessage}
                />
            )}
        </Grid>
    );
};

export default LegacyLoginForm;
